import React, { useEffect, useState, useMemo } from "react";
import DataTable from "react-data-table-component";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import { Link, useNavigate } from "react-router-dom";
import {
  getAllPurchaseOrders,
  deletePurchaseOrder,
  getSinglePurchaseOrder,
} from "../../services/services";
import Loading from "../../components/loading";
import moment from "moment";
import { toastError } from "../../utils";
import PartyCardDetail from "../party/partyCardDetail";
import Pagination from "../../components/Pagination";
import { confirmDelete } from "../../components/confirmDelete";
import { Plus } from "lucide-react";
import { toast } from "react-toastify";

const PurchaseOrderList = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(6);
  const [isConvertedEdit, setIsConvertedEdit] = useState(false);

  const navigate = useNavigate();

  /* ===== FETCH ORDERS ===== */
  const fetchOrders = async (payload = {}) => {
    setLoading(true);
    try {
      const response = await getAllPurchaseOrders(payload);
      if (response.data.status) {
        setOrders(response.data.response.purchaseOrders || []);
        setTotalPages(response.data.totalPages || 1);
        setCurrentPage(response.data.page || 1);
      } else {
        toastError("Failed to fetch purchase orders");
        setOrders([]);
      }
    } catch (error) {
      toastError(
        error.response?.data?.message || "Failed to fetch purchase orders",
      );
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };
  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#0b2b6d",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        backgroundColor: "#0b2b6d",
        color: "#ffffff",
        // fontWeight: "600",
        // fontSize: "14px",
        textTransform: "uppercase",
      },
    },
  };

  useEffect(() => {
    fetchOrders({ page: currentPage, limit });
  }, [currentPage, limit]);

  /* ===== DELETE ===== */
  const handleDelete = async (row) => {
    const vendor = row.vendor?.name || "Vendor";
    const invoice = row.invoiceNumber || row._id.slice(-6);

    const confirmation = await confirmDelete(`${vendor} (${invoice})`);
    if (!confirmation) return;

    try {
      await deletePurchaseOrder(row._id);
      toast.success("Purchase order deleted Successfully");
      fetchOrders({ page: currentPage, limit });
      // window.location.reload();
    } catch (error) {
      toastError(error.response?.data?.message || "Delete failed");
    }
  };

  /* ===== SELECT ORDER ===== */
  const handleSelectOrder = async (order) => {
    try {
      setLoading(true);

      const res = await getSinglePurchaseOrder(order._id);
      const po = res?.data?.response;
      setIsConvertedEdit(po.isConverted);

      if (!po) {
        toastError("Purchase order not found");
        return;
      }

      const ledgerEntry = {
        _id: po._id,
        referenceId: po._id,
        referenceType: "purchase-order",
        invoiceNumber: po.invoiceNumber || `PO-${po._id}`,
        date: po.createdAt,

        totalAmount: Number(po.totalAmount) || 0,
        dueAmount: Number(po.dueAmount) || 0,
        receivedAmount: Number(po.paidAmount) || 0,

        party: {
          _id: po.vendor?._id || "",
          name: po.vendor?.name || "No Vendor",
          mobile: po.vendor?.mobile || "",
          partyType: "vendor",

          credit_balance: Number(po.vendor?.credit_balance || 0),
        },

        items: (po.items || []).map((item) => {
          const purchasePrice =
            item.purchasePrice ??
            item.productId?.batches?.[item.productId.batches.length - 1]
              ?.purchasePrice ??
            0;

          return {
            _id: item._id,
            productId: item.productId,
            itemName: item.itemName || item.name || item.productId?.name,
            quantity: Number(item.quantity),
            purchasePrice: Number(purchasePrice),
            gstRate: Number(item.gstRate || item.productId?.gstRate || 0),
            taxType: item.taxType || "exclusive",
            unit: item.unit,
          };
        }),

        otherExpenses: po.otherExpenses || [],
        services: po.services || [],
        discountAmount: po.discountAmount || 0,
        discountType: po.discountType,
        discountValue: po.discountValue,
        totalBeforeDiscount: po.totalBeforeDiscount || po.totalAmount,
      };

      setSelectedOrder(ledgerEntry);
    } catch (error) {
      toastError("Failed to fetch purchase order details");
    } finally {
      setLoading(false);
    }
  };

  /* ===== DATA TABLE COLUMNS ===== */
  const columns = useMemo(
    () => [
      {
        name: "PO No.",
        selector: (row) => row.invoiceNumber || "—",
        sortable: true,
      },
      {
        name: "Vendor Name",
        selector: (row) => row.vendor?.name || "—",
      },
      {
        name: "Item",
        selector: (row) =>
          row.items?.length ? row.items[0]?.itemName : "No Item",
      },
      {
        name: "Total Amount",
        cell: (row) => `₹ ${row.totalAmount?.toFixed(2) || "0.00"}`,
      },
      {
        name: "Purchased",
        cell: (row) => (
          <span className={row.isConverted ? "text-success" : "text-danger"}>
            {row.isConverted ? "Yes" : "No"}
          </span>
        ),
      },
      {
        name: "Order Date",
        selector: (row) => moment(row.orderDate).format("DD MMM, YYYY"),
      },
      {
        name: "Action",
        // center: true,
        width: "15rem",
        cell: (row) => (
          <div className="d-flex gap-2">
            <button
              className={`btn btn-light btn-sm shadow-sm text-primary border text-nowrap ${
                row.isConverted ? "disabled-btn" : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/purchases/create/${row._id}`);
              }}
              disabled={row.isConverted}
            >
              {row.isConverted ? "Purchased" : "Convert to purchase"}
            </button>

            <button
              className="btn btn-light btn-sm shadow-sm text-danger border"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row);
              }}
            >
              <i className="bi bi-trash" />
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <div id="main">
        <div className="d-flex justify-content-between mb-4 mt-2">
          <h2 className="custom-title mb-0">Manage Purchase Orders</h2>
          <Link to="/purchases/order">
            <button className="btn btn-primary btn-sm">
              Create Purchase Order
            </button>
          </Link>
        </div>

        <div className="card">
          <div className="card-body p-0 rounded">
            <DataTable
              columns={columns}
              data={orders}
              progressPending={loading}
              customStyles={customStyles}
              progressComponent={<Loading />}
              noDataComponent={
                <div className="text-center p-5 text-muted">
                  <i className="bi bi-inbox display-1"></i>
                  <p>No purchase orders found</p>
                </div>
              }
              highlightOnHover
              striped
              onRowClicked={handleSelectOrder}
            />
          </div>
        </div>
        {orders.length > 0 && (
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            limit={limit}
            onPageChange={setCurrentPage}
            onLimitChange={setLimit}
          />
        )}
        {selectedOrder && (
          <>
            <div
              className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-25"
              style={{ zIndex: 1049 }}
              onClick={() => setSelectedOrder(null)}
            />
            <PartyCardDetail
              ledgerEntry={selectedOrder}
              isConvertedEdit={isConvertedEdit}
              onClose={() => setSelectedOrder(null)}
              onDeleteSuccess={() => {
                setSelectedOrder(null);
                fetchOrders({ page: currentPage, limit });
              }}
            />
          </>
        )}
      </div>
    </>
  );
};

export default headerLayoutWrapper(PurchaseOrderList);
